import { cn } from '@/lib/utils/cn'
import { classifyMargin, getMarginThresholds, type MarginThresholds } from '@/lib/utils/margin-thresholds'

/** @deprecated use `MarginThresholds` from `@/lib/utils/margin-thresholds`. */
export type MarginIndicatorThresholds = MarginThresholds

export interface MarginIndicatorProps {
  /** Percentage value, e.g. 34.2 for 34.2%. Negative values paint as danger. */
  value: number
  thresholds?: MarginThresholds
  /** Show the track bar. Default true. */
  showBar?: boolean
  /** Override the rendered value text. */
  renderValue?: (value: number) => React.ReactNode
  className?: string
}

const colorText: Record<string, string> = {
  negative: 'text-danger-text',
  danger: 'text-danger-text',
  warning: 'text-warning-text',
  success: 'text-success-text',
}

const colorFill: Record<string, string> = {
  negative: 'bg-danger-dot',
  danger: 'bg-danger-dot',
  warning: 'bg-warning-dot',
  success: 'bg-success-dot',
}

/**
 * Visualizes gross margin % with semantic thresholds baked in.
 *
 * Used in Sales Quoting + Margins module — the canonical case where
 * "sales reps see margin the moment they pick a vendor" (Done-bar §5).
 */
export function MarginIndicator({
  value,
  thresholds = getMarginThresholds(),
  showBar = true,
  renderValue,
  className,
}: MarginIndicatorProps) {
  const bucket = classifyMargin(value, thresholds)
  const fillPercent = value < 0 ? 5 : Math.min(100, value)
  return (
    <span
      className={cn(
        'inline-flex min-w-[var(--margin-indicator-min-width)] items-center gap-2 tabular-nums',
        colorText[bucket],
        className,
      )}
      data-margin-bucket={bucket}
    >
      {showBar ? (
        <span className="relative h-1.5 w-[var(--margin-indicator-track-width)] overflow-hidden rounded-xs bg-muted">
          <span
            className={cn(
              'block h-full rounded-xs transition-[width,background] duration-normal ease-standard',
              colorFill[bucket],
            )}
            style={{ width: `${fillPercent}%` }}
          />
        </span>
      ) : null}
      <span className="min-w-12 text-right text-sm font-medium">
        {renderValue ? renderValue(value) : `${value.toFixed(1)}%`}
      </span>
    </span>
  )
}
